import React from 'react';
import { Drawer as AntDrawer, DrawerProps as AntDrawerProps } from 'antd';
import { createGlobalStyle, useTheme } from 'styled-components';
import { DrawerHeader } from './next/header';
import { DrawerBody } from './next/body';
import { DrawerFooter } from './next/footer';

const NextDrawerStyles = createGlobalStyle`
  .ant-drawer-next {
    .ant-drawer-content-wrapper {
      box-shadow: none;
      border-left: 1px solid ${({ theme }) => theme.token.next.colorBorder};
    }
    .ant-drawer-mask {
      background-color: ${({ theme }) => theme.token.colorBgMask};
    }
  }
`;

interface DrawerComponentProps extends AntDrawerProps {
  variant?: 'next';
  maskVariant?: 'default' | 'transparent' | 'none';
}

/** `calc(100vw - <width>)` needs an absolute length. A percentage resolves
 * against the root's own width (wrong offset), and `auto` makes the whole
 * calc invalid so the browser drops the declaration — the drawer would then
 * cover the docked panel entirely. Anything but px/number gets no offset,
 * which is simply the pre-dock behavior. */
const toPxLength = (width: AntDrawerProps['width']): string | undefined => {
  if (typeof width === 'number') {
    return `${width}px`;
  }
  return typeof width === 'string' && /^\d+(\.\d+)?px$/.test(width.trim())
    ? width.trim()
    : undefined;
};

/**
 * Right-anchored drawers stop at the app's right dock instead of covering it
 * — until the viewport can't hold both. Then the drawer keeps its designed
 * width and slides OVER the dock by exactly the shortfall (overlap-degrade):
 * a squeezed drawer reads as broken while looking functional, whereas a
 * partially covered chat is visibly "behind" and the way out is obvious.
 *
 * --app-right-dock-width is published on <html> by whichever feature docks a
 * panel on the right edge (today: the RudderAI chat); 0 when nothing is
 * docked. Shifting the drawer ROOT moves the panel and clips the mask in one
 * go, so the docked panel stays visible and clickable when there is room.
 */
// ts-prune-ignore-next -- consumed by __tests__, which are outside the deadcode project
export const rightDockOffset = (
  placement: AntDrawerProps['placement'],
  width: AntDrawerProps['width'],
) => {
  if ((placement ?? 'right') !== 'right') {
    return undefined;
  }
  const widthValue = toPxLength(width);
  if (!widthValue) {
    return undefined;
  }
  return {
    right: `clamp(0px, calc(100vw - ${widthValue}), var(--app-right-dock-width, 0px))`,
    // Below the chat panel (z 950 in rudderAI/styles.ts): when the drawer
    // overlap-degrades into the chat's column, the chat the user opened
    // stays on top. Still far above page chrome, below antd's 1000+ band.
    zIndex: 940,
  };
};

/**
 * Safety net for drawers wider than the viewport itself: with the offset at
 * its 0 floor the root spans the full viewport, so 100% caps the wrapper at
 * the screen instead of hanging past the left edge. In every other case the
 * offset already guarantees the designed width fits the root.
 */
const rightDockWidthCap = (placement: AntDrawerProps['placement']) =>
  (placement ?? 'right') === 'right' ? { maxWidth: '100%' } : undefined;

const DrawerBase = ({
  title,
  open,
  onClose,
  children,
  styles,
  placement,
  variant,
  width,
  ...props
}: DrawerComponentProps) => {
  const { token } = useTheme();

  if (variant === 'next') {
    return (
      <>
        <NextDrawerStyles />
        <AntDrawer
          open={open}
          onClose={onClose}
          placement={placement}
          width={width ?? 320}
          closable={false}
          title={null}
          mask={props.maskVariant !== 'none'}
          styles={{
            mask:
              props.maskVariant === 'transparent' ? { backgroundColor: 'transparent' } : undefined,
            header: { display: 'none' },
            body: {
              padding: 0,
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
            },
            wrapper: rightDockWidthCap(placement),
          }}
          {...props}
          rootStyle={{ ...rightDockOffset(placement, width ?? 320), ...props.rootStyle }}
          rootClassName="ant-drawer-next"
        >
          {children}
        </AntDrawer>
      </>
    );
  }

  const defaultBodyStyle = {
    padding: `${token.paddingLG}px`,
    borderBottom: `1px solid ${token.colorBorder}`,
    color: `${token.colorText}`,
  };

  return (
    <AntDrawer
      title={title}
      placement={placement}
      closable={false}
      onClose={onClose}
      open={open}
      width={width ?? 480}
      styles={{
        header: {
          padding: `${token.paddingLG}px`,
          borderBottom: `1px solid ${token.colorBorder}`,
          color: `${token.colorText}`,
        },
        body: defaultBodyStyle,
        ...styles,
        wrapper: { ...rightDockWidthCap(placement), ...styles?.wrapper },
      }}
      {...props}
      rootStyle={{ ...rightDockOffset(placement, width ?? 480), ...props.rootStyle }}
    >
      {children}
    </AntDrawer>
  );
};

export const Drawer = Object.assign(DrawerBase, {
  Header: DrawerHeader,
  Body: DrawerBody,
  Footer: DrawerFooter,
});

export type { DrawerProps } from 'antd';
